import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { camBasis, createWorldObject, updateWorldObject, deleteWorldObject } from '../services/worldObjects';

// RecordCardsPanel — hang the record shop's featured sleeves, and keep it.
//
// Same two corrections as the resident panels. It no longer dispatches synthetic
// keydown events: the record store is first-person and its camera already
// listens for arrows and WASD on window, so a placement handler bound to those
// keys moved the camera and the sleeve together. And it no longer writes to
// localStorage, which only ever arranged the shop for one browser on one
// machine — Save writes a row to world_objects, so the arrangement is the shop.
//
// THE SLOT COUNT IS THE SHELF. Tracks are sliced to the number of placed slots,
// so "add slot" is how the wall gets fuller — not a bigger number somewhere.
const ZONE = 'record-store';
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 214, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const arrow = { ...btn, padding: '9px 0' };
const r2 = (n) => Math.round(n * 100) / 100;

export default function RecordCardsPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'cards';
  const interior = useWorldStore((s) => s.interior);
  const objects = useWorldStore((s) => s.worldObjects);
  const sel = useWorldStore((s) => s.worldObjectSel);
  const dirty = useWorldStore((s) => s.worldObjectDirty);
  const setSel = useWorldStore((s) => s.setWorldObjectSel);
  const patch = useWorldStore((s) => s.patchWorldObject);
  const addObj = useWorldStore((s) => s.addWorldObject);
  const removeObj = useWorldStore((s) => s.removeWorldObject);
  const markSaved = useWorldStore((s) => s.markWorldObjectSaved);

  const [fine, setFine] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  if (!ON) return null;

  if (interior !== ZONE) {
    return (
      <div style={panel}>
        <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>Record sleeves</div>
        <div style={{ opacity: 0.65, fontSize: 11.5, lineHeight: 1.4 }}>Step into the record store — these hang on its wall.</div>
      </div>
    );
  }

  const slots = objects.filter((o) => o.zone_id === ZONE && o.kind === 'card').sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  const cur = slots.find((o) => o.object_id === sel) || null;
  const step = fine ? 0.02 : 0.08;

  const edit = (fn) => cur && patch(cur.object_id, fn(cur));
  const move = (axis, dir) => edit((o) => {
    const [x, y, z] = o.pos; const d = step * dir;
    if (axis === 'across') return { pos: [r2(x + camBasis.rx * d), y, r2(z + camBasis.rz * d)] };
    if (axis === 'depth') return { pos: [r2(x + camBasis.fx * d), y, r2(z + camBasis.fz * d)] };
    return { pos: [x, r2(y + d), z] };
  });
  const turn = (dir) => edit((o) => ({ rotY: r2(o.rotY + 0.06 * dir) }));
  const resize = (dir) => edit((o) => ({ size: Math.max(0.12, r2(o.size + 0.04 * dir)) }));

  // A new slot lands beside the last one rather than at the origin — a sleeve
  // inside a wall is one you have to hunt for before you can place it.
  const addSlot = () => {
    const last = slots[slots.length - 1];
    const pos = last ? [r2(last.pos[0] + 0.6), last.pos[1], r2(last.pos[2])] : [0, 1.9, -2];
    addObj({ zone_id: ZONE, kind: 'card', ref: null, slot: slots.length, pos, rotY: last?.rotY || 0, size: last?.size || 0.5 });
  };

  const save = async () => {
    if (!cur) return;
    setBusy(true); setMsg('');
    try {
      const body = { zone_id: ZONE, kind: 'card', ref: null, slot: cur.slot ?? 0, pos: cur.pos, rotY: cur.rotY, size: cur.size };
      const isNew = typeof cur.object_id === 'string';
      const saved = isNew ? await createWorldObject(body) : await updateWorldObject(cur.object_id, body);
      if (saved?.object_id) { patch(cur.object_id, { object_id: saved.object_id }); markSaved(cur.object_id); setSel(saved.object_id); markSaved(saved.object_id); }
      setMsg('saved');
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  // Saving every slot at once, because a shelf is arranged as a whole and
  // pressing Save eight times is how one gets forgotten.
  const saveAll = async () => {
    setBusy(true); setMsg('');
    try {
      for (const o of slots) {
        const body = { zone_id: ZONE, kind: 'card', ref: null, slot: o.slot ?? 0, pos: o.pos, rotY: o.rotY, size: o.size };
        const saved = typeof o.object_id === 'string' ? await createWorldObject(body) : await updateWorldObject(o.object_id, body);
        if (saved?.object_id) { patch(o.object_id, { object_id: saved.object_id }); markSaved(o.object_id); markSaved(saved.object_id); }
      }
      setMsg('shelf saved');
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const remove = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      if (typeof cur.object_id === 'number') await deleteWorldObject(cur.object_id);
      removeObj(cur.object_id);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const isDirty = cur && dirty.includes(cur.object_id);

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>
        Record sleeves · {slots.length} slot{slots.length === 1 ? '' : 's'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginBottom: 8 }}>
        {slots.map((o) => (
          <button key={o.object_id} onClick={() => setSel(o.object_id)}
            style={{ ...btn, padding: '6px 0', fontSize: 12,
              background: o.object_id === sel ? '#ffb25e' : 'rgba(255,178,94,0.14)',
              color: o.object_id === sel ? '#1c1206' : '#ffd27d' }}>
            {(o.slot ?? 0) + 1}{dirty.includes(o.object_id) ? '•' : ''}
          </button>
        ))}
      </div>
      <button style={{ ...btn, width: '100%', marginBottom: 10 }} onClick={addSlot}>+ add slot</button>

      {cur && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 4 }}>
            <span />
            <button style={arrow} onClick={() => move('height', 1)}>↑</button>
            <span />
            <button style={arrow} onClick={() => move('across', -1)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>height</div>
            <button style={arrow} onClick={() => move('across', 1)}>→</button>
            <span />
            <button style={arrow} onClick={() => move('height', -1)}>↓</button>
            <span />
          </div>

          <div style={{ display: 'flex', gap: 5, margin: '8px 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('depth', 1)}>▲ closer</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('depth', -1)}>further ▼</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(1)}>⟲ rotate</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(-1)}>rotate ⟳</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 8px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(-1)}>− size</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(1)}>size +</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 8px', cursor: 'pointer', opacity: 0.9 }}>
            <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
            fine steps
          </label>

          <div style={{ opacity: 0.5, fontSize: 10, marginBottom: 8, fontVariantNumeric: 'tabular-nums' }}>
            {cur.pos.map(r2).join(', ')} · rot {r2(cur.rotY)} · {r2(cur.size)}
          </div>

          <div style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
            <button disabled={busy}
              style={{ ...btn, flex: 1, background: isDirty ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: isDirty ? '#1c1206' : '#ffd27d' }}
              onClick={save}>{busy ? '…' : isDirty ? 'Save *' : 'Save'}</button>
            <button style={{ ...btn, width: 52 }} onClick={remove} disabled={busy}>del</button>
          </div>
          <button style={{ ...btn, width: '100%' }} onClick={saveAll} disabled={busy}>save whole shelf</button>
          {msg && <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85, color: /saved/.test(msg) ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}
        </>
      )}
    </div>
  );
}
